(function (window, document) {
  'use strict';

  var calendlyLoader;

  function loadCalendly() {
    if (window.Calendly) return Promise.resolve(window.Calendly);
    if (calendlyLoader) return calendlyLoader;
    var script = document.createElement('script');
    var pending = new Promise(function (resolve, reject) {
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = function () {
        if (window.Calendly) resolve(window.Calendly);
        else reject(new Error('Calendar unavailable'));
      };
      script.onerror = reject;
      document.head.appendChild(script);
    }).catch(function (error) {
      script.remove();
      if (calendlyLoader === pending) calendlyLoader = undefined;
      throw error;
    });
    calendlyLoader = pending;
    return calendlyLoader;
  }

  function createId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'bd-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function initialize(root) {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    var handoff;
    var revision = 0;
    var calendarAttempt;
    var preparation = { key: null, identity: 'unknown', session: null, phase: 'resolving', attempt: null };
    var fallback = root.querySelector('[data-bd-session-fallback]');
    var form = root.querySelector('[data-bd-session-form]');
    var steps = Array.from(form.querySelectorAll('[data-bd-session-step]'));
    var back = form.querySelector('[data-bd-session-back]');
    var next = form.querySelector('[data-bd-session-next]');
    var submit = form.querySelector('[data-bd-session-submit]');
    var errorNode = form.querySelector('[data-bd-session-error]');
    var booking = root.querySelector('[data-bd-session-booking]');
    var calendar = root.querySelector('[data-bd-session-calendar]');
    var calendarHelp = root.querySelector('[data-bd-session-calendar-help]');
    var calendarHelpCopy = calendarHelp.textContent;
    var current = 0;

    // Identity enriches preparation; it never decides whether someone may answer.
    // This controller owns readiness, the immutable retry attempt and booking.
    function selectedContext() {
      handoff = window.BDMasterclassHandoff;
      try {
        if (handoff) return handoff.candidate(root.dataset.cohort);
      } catch (error) { /* Preparation remains available without its handoff. */ }
      return { key: 'unavailable', state: 'unavailable' };
    }

    function setIdentity(session) {
      preparation.session = session || null;
      preparation.identity = session ? 'known' : 'unknown';
      if (calendarAttempt && calendarAttempt.key !== bookingIdentity()) resetCalendar();
      root.dataset.identity = preparation.identity;
      var identityNode = root.querySelector('[data-bd-session-identity]');
      identityNode.hidden = !session;
      identityNode.textContent = session
        ? (session.name ? 'For ' + session.name + ' · ' + session.email : 'For ' + session.email)
        : '';
      root.querySelector('[data-bd-session-privacy-copy]').textContent = session
        ? "I'll save these answers with your registration to prepare for your session."
        : "I'll use these answers to prepare for your session. Your name and email will be collected in the calendar.";
    }

    function setPhase(phase) {
      preparation.phase = phase;
      root.dataset.sessionState = phase;
      fallback.hidden = phase !== 'resolving';
      form.hidden = phase === 'resolving' || phase === 'completed';
      booking.hidden = phase !== 'completed';
      booking.toggleAttribute('inert', phase !== 'completed');
      booking.setAttribute('aria-hidden', String(phase !== 'completed'));
      var saving = phase === 'saving';
      form.setAttribute('aria-busy', String(saving));
      form.querySelectorAll('button').forEach(function (button) { button.disabled = saving; });
      steps.forEach(function (step, index) { step.disabled = phase !== 'ready' || index !== current; });
      submit.textContent = saving ? 'Saving your answers…' : 'Save answers & choose a time →';
    }

    async function resolveSession() {
      var selected = selectedContext();
      var sameContext = preparation.key === selected.key;
      if (sameContext && preparation.phase === 'saving') return;
      if (sameContext && preparation.phase === 'completed') { showBooking(false); return; }
      var attempt = ++revision;
      if (!sameContext) {
        form.reset();
        current = 0;
        preparation = { key: selected.key, identity: 'unknown', session: null, phase: 'resolving', attempt: null };
        resetCalendar();
      }
      setPhase('resolving');
      var result;
      try { result = handoff ? await handoff.resolve(root.dataset.cohort) : selected; }
      catch (error) { result = { key: selected.key, state: 'retryable-error' }; }
      if (attempt !== revision) return;
      if (selected.key !== selectedContext().key) { resolveSession(); return; }
      var known = (result.state === 'ready' || result.state === 'completed') && result.session;
      // A failed save retries its selected identity. A terminal credential
      // failure may downgrade; a context outage alone must not duplicate it.
      var pending = preparation.attempt;
      if (pending && pending.identity === 'unknown') known = null;
      else if (pending && pending.identity === 'known' && (result.state === 'retryable-error' || result.state === 'unavailable')) known = preparation.session;
      setIdentity(known);
      if (pending) pending.identity = preparation.identity;
      if (known && result.state === 'completed') showBooking(false);
      else {
        setPhase('ready');
        showStep(current, false);
      }
    }

    function showStep(index, focus) {
      current = index;
      steps.forEach(function (step, stepIndex) {
        step.hidden = stepIndex !== current;
        step.disabled = stepIndex !== current;
      });
      back.hidden = current === 0;
      next.hidden = current === steps.length - 1;
      submit.hidden = current !== steps.length - 1;
      root.querySelector('[data-bd-session-progress]').textContent = 'Question ' + (current + 1) + ' of ' + steps.length;
      root.querySelector('[data-bd-session-meter]').value = current + 1;
      errorNode.hidden = true;
      if (focus) steps[current].querySelector('legend').focus();
      if (current >= 1) prepareCalendar(false);
    }

    function showError(message) {
      errorNode.textContent = message;
      errorNode.hidden = false;
    }

    function validateStep() {
      var urlInput = steps[current].querySelector('input[name="storeUrl"]');
      if (urlInput) {
        var value = urlInput.value.trim();
        if (value) {
          try {
            var url = new URL(value);
            if (!/^https?:$/.test(url.protocol) || !url.hostname.includes('.') || url.username || url.password) throw new Error('Invalid URL');
          } catch (error) {
            showError('Enter a website address starting with https://, or leave this blank.');
            urlInput.focus();
            return false;
          }
        }
      } else if (!steps[current].querySelector('input:checked')) {
        showError('Choose an answer to continue. Every answer is welcome.');
        steps[current].querySelector('input').focus();
        return false;
      }
      return true;
    }

    function advance() {
      if (preparation.phase === 'ready' && current < steps.length - 1 && validateStep()) showStep(current + 1, true);
    }

    function answers() {
      var out = {};
      ['storeStatus', 'monthlyOrders', 'focus'].forEach(function (name) {
        var input = form.querySelector('input[name="' + name + '"]:checked');
        out[name] = input ? input.value : '';
      });
      out.storeUrl = form.querySelector('input[name="storeUrl"]').value.trim();
      return out;
    }

    function bookingIdentity() {
      var session = preparation.session;
      return JSON.stringify([preparation.key, session ? [session.name, session.email] : null]);
    }

    // One owner manages idle -> loading -> initialized/failed. Revealing a
    // completed form never recreates or reparents a matching, loaded iframe.
    function resetCalendar() {
      calendarAttempt = undefined;
      calendar.replaceChildren();
      booking.removeAttribute('data-preloading');
      calendarHelp.textContent = calendarHelpCopy;
    }

    function prepareCalendar(retry) {
      var key = bookingIdentity();
      if (calendarAttempt && calendarAttempt.key === key && (calendarAttempt.phase !== 'failed' || !retry)) return;
      resetCalendar();
      try {
        var url = new URL(root.dataset.bookingUrl);
        if (url.protocol !== 'https:' || url.hostname !== 'calendly.com') return;
      } catch (error) { return; }
      var attempt = { key: key, phase: 'loading', session: preparation.session };
      calendarAttempt = attempt;
      booking.setAttribute('data-preloading', '');
      function isCurrent() {
        return root.isConnected && calendarAttempt === attempt && preparation.key === selectedContext().key;
      }
      loadCalendly().then(function (calendly) {
        if (!isCurrent()) return;
        var options = { url: root.dataset.bookingUrl, parentElement: calendar };
        if (attempt.session) options.prefill = { name: attempt.session.name, email: attempt.session.email };
        calendly.initInlineWidget(options);
        attempt.phase = 'initialized';
      }).catch(function () {
        if (!isCurrent()) return;
        attempt.phase = 'failed';
        calendar.replaceChildren();
        calendarHelp.textContent = 'The calendar could not load here. Use the link above to choose a time.';
      });
    }

    function showBooking(focus) {
      setPhase('completed');
      root.querySelector('[data-bd-session-booking-status]').textContent = preparation.identity === 'known'
        ? 'Your answers are saved. Pick a time below to confirm your session.'
        : 'Your answers are saved. Choose a time, then add your name and email to confirm your session.';
      if (focus !== false) root.querySelector('[data-bd-session-booking-title]').focus();
      prepareCalendar(true);
    }

    function prefillReadyCalendar(event) {
      var attempt = calendarAttempt;
      var frame = calendar.querySelector('iframe');
      if (!attempt || !attempt.session || attempt.prefillSent || !root.isConnected ||
          preparation.key !== selectedContext().key || event.origin !== 'https://calendly.com' ||
          !frame || event.source !== frame.contentWindow || !event.data ||
          event.data.event !== 'calendly.event_type_viewed') return;
      // widget.js sends prefill on iframe load, which can precede the app's
      // listener. Resend its native message once when the scheduler is ready.
      frame.contentWindow.postMessage({
        event: 'calendly.prefill',
        payload: { name: attempt.session.name, email: attempt.session.email }
      }, 'https://calendly.com');
      attempt.prefillSent = true;
    }

    function isIdentityRejection(payload) {
      var code = payload && payload.code;
      return code === 'INVALID_PLANNING_SESSION' ||
        code === 'EXPIRED_PLANNING_SESSION' || code === 'PLANNING_COHORT_MISMATCH';
    }

    async function saveAnswers(attempt, submittedAnswers) {
      var body = { submissionId: attempt.id, answers: submittedAnswers };
      if (preparation.identity === 'known') body.token = preparation.session.token;
      else { body.identity = 'unknown'; body.cohort = root.dataset.cohort; }
      var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      var timeout = window.setTimeout(function () { if (controller) controller.abort(); }, 20000);
      try {
        var response = await window.fetch(form.action, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'accept': 'application/json' },
          signal: controller ? controller.signal : undefined,
          body: JSON.stringify(body)
        });
        return { response: response, payload: await response.json() };
      } finally { window.clearTimeout(timeout); }
    }

    next.addEventListener('click', advance);
    back.addEventListener('click', function () {
      if (preparation.phase === 'ready' && current > 0) showStep(current - 1, true);
    });
    form.querySelector('[data-bd-session-no-url]').addEventListener('click', function () {
      form.querySelector('input[name="storeUrl"]').value = '';
      advance();
    });
    form.addEventListener('click', function (event) {
      // Pointer activation belongs to the whole option. WebKit forwards label
      // clicks to the input with detail 0, so retain the original pointer intent.
      // Keyboard selections stay here until Next; the final answer needs Submit.
      var option = event.target.closest('.bd-session__option');
      if (!option || event.detail === 0 || preparation.phase !== 'ready') return;
      var input = option.querySelector('input[type="radio"]');
      var activatedStep = current;
      if (event.target === input) {
        advance();
      } else {
        // Wait for the label's native selection. If its forwarded input click
        // already advanced (Chromium), the captured step prevents a second jump.
        window.setTimeout(function () {
          if (current === activatedStep && input.checked) advance();
        }, 0);
      }
    });
    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (preparation.phase !== 'ready') return;
      if (preparation.key !== selectedContext().key) { resolveSession(); return; }
      if (current !== steps.length - 1) { advance(); return; }
      if (!validateStep()) return;
      errorNode.hidden = true;
      var submittedAnswers = answers();
      var fingerprint = JSON.stringify({ cohort: root.dataset.cohort, answers: submittedAnswers });
      if (!preparation.attempt || preparation.attempt.fingerprint !== fingerprint) {
        preparation.attempt = { id: createId(), fingerprint: fingerprint, identity: preparation.identity };
      }
      var attempt = preparation.attempt;
      var submittedRevision = revision;
      setPhase('saving');
      try {
        // Only a terminal identity rejection gets one unknown resend. Preserve
        // the ID and answers so retries remain the same logical submission.
        for (var send = 0; send < 2; send += 1) {
          var result = await saveAnswers(attempt, submittedAnswers);
          if (submittedRevision !== revision) return;
          if (preparation.key !== selectedContext().key) { resolveSession(); return; }
          if (preparation.identity === 'known' && isIdentityRejection(result.payload)) {
            setIdentity(null);
            attempt.identity = 'unknown';
            continue;
          }
          if (!result.response.ok || !result.payload || !result.payload.ok || !result.payload.result || result.payload.result.saved !== true) throw new Error('Answers were not saved');
          showBooking();
          return;
        }
        throw new Error('Answers were not saved');
      } catch (error) {
        if (submittedRevision !== revision) return;
        if (preparation.key !== selectedContext().key) { resolveSession(); return; }
        setPhase('ready');
        showError("We couldn't save your answers just now. Your answers are still here. Please try again.");
      }
    });
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && root.isConnected) resolveSession();
    });
    window.addEventListener('message', prefillReadyCalendar);
    ['hashchange', 'popstate'].forEach(function (type) {
      window.addEventListener(type, function () {
        if (root.isConnected) resolveSession();
      });
    });
    resolveSession();
  }

  document.querySelectorAll('[data-bd-masterclass-session]').forEach(initialize);
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-bd-masterclass-session]').forEach(initialize);
  });
})(window, document);
